import { writeFileXLSX, utils } from "xlsx";
import {
  WEEKDAY_LABELS,
  formatMinutes,
  formatBalance,
  shortDate,
  dayOffLabel,
  formatPeriodLabel,
  periodFileSuffix,
} from "./timeClock.js";

// Planilha do espelho de ponto: a do colaborador escolhido e a "Exportar todos" em
// /c/time-clock, e a do próprio colaborador em /e/time-clock.

// O backend passou a devolver period_* e mantém os month_* como alias durante o
// rollout; ler os dois evita depender de qual versão respondeu.
export function bankEventsMinutes(mirrorData) {
  return mirrorData.bank.period_events_minutes ?? mirrorData.bank.month_events_minutes;
}

export function mirrorToSheetRows(mirrorData) {
  const rows = mirrorData.days.map((day) => ({
    Dia: `${shortDate(day.date)} (${WEEKDAY_LABELS[day.weekday]})`,
    Batidas: day.entries.map((entry) => `${entry.time}${entry.source === "ADJUSTMENT" ? "*" : ""}`).join(", "),
    Trabalhado: day.is_future ? "-" : formatMinutes(day.worked_minutes),
    Esperado: formatMinutes(day.expected_minutes),
    Saldo: day.balance_minutes === null ? (day.in_progress ? "Em andamento" : "-") : formatBalance(day.balance_minutes),
    Observações: [
      day.holiday_name,
      day.is_day_off ? dayOffLabel(day.day_off_name, day.day_off_hours) : null,
      day.inconsistent ? "Batida ímpar" : null,
    ]
      .filter(Boolean)
      .join(" · "),
  }));

  rows.push({});
  rows.push({
    Dia: "TOTAL DO PERÍODO",
    Trabalhado: formatMinutes(mirrorData.totals.worked_minutes),
    Esperado: formatMinutes(mirrorData.totals.expected_minutes),
    Saldo: formatBalance(mirrorData.totals.balance_minutes),
  });
  rows.push({ Dia: "Banco anterior", Saldo: formatBalance(mirrorData.bank.previous_balance_minutes) });
  rows.push({ Dia: "Acertos no período", Saldo: formatBalance(bankEventsMinutes(mirrorData)) });
  rows.push({ Dia: "BANCO DE HORAS FINAL", Saldo: formatBalance(mirrorData.bank.final_balance_minutes) });

  return rows;
}

/** Nome de aba do Excel: sem os caracteres proibidos e com até 31 caracteres. */
export function sheetName(name) {
  const cleaned = (name || "Colaborador").replace(/[\\/?*[\]:]/g, "");
  return cleaned.slice(0, 31);
}

/**
 * Período de um resultado do backend, normalizado para { start, end }. Vem do
 * próprio retorno, para que a planilha descreva exatamente o intervalo que o
 * backend calculou; `fallback` ({ start, end }) só vale sem ele.
 */
export function periodOf(result, fallback) {
  const range = result?.period;
  return range ? { start: range.start_date, end: range.end_date } : fallback;
}

export function appendMirrorSheet(workbook, mirrorData, name, fallbackPeriod) {
  // Duas linhas de cabeçalho antes da tabela para que o arquivo registre de
  // quem e de que período ele é — o nome do arquivo sozinho se perde.
  const { start, end } = periodOf(mirrorData, fallbackPeriod);
  const ws = utils.aoa_to_sheet([[mirrorData.user?.name || "Colaborador"], [`Período: ${formatPeriodLabel(start, end)}`], []]);
  utils.sheet_add_json(ws, mirrorToSheetRows(mirrorData), { origin: -1 });
  ws["!cols"] = [{ wch: 16 }, { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 30 }];
  utils.book_append_sheet(workbook, ws, sheetName(name));
}

/** Nome do arquivo do espelho de uma pessoa: "espelho_ponto_joana_martins_2026-10.xlsx". */
export function mirrorFileName(mirrorData, fallbackPeriod) {
  const { start, end } = periodOf(mirrorData, fallbackPeriod);
  const person = (mirrorData.user?.name || "colaborador").toLowerCase().replace(/\s+/g, "_");
  return `espelho_ponto_${person}_${periodFileSuffix(start, end)}.xlsx`;
}

/** Baixa a planilha do espelho de uma pessoa. */
export function exportMirror(mirrorData, fallbackPeriod) {
  const wb = utils.book_new();
  appendMirrorSheet(wb, mirrorData, mirrorData.user?.name, fallbackPeriod);
  writeFileXLSX(wb, mirrorFileName(mirrorData, fallbackPeriod));
}
